/**
 * @file ClientSettingSelfRegistration.js
 * @description Component that allows user to toggle self registration.
 */

import { useState, useEffect } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
} from '../../services/clientService';
import Toast from '../ui/toasts/Toast';
import { SecurityAttributes, ClientOptionEnum } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

const ClientSettingSelfRegistration = ({
    onSelfRegistrationChange,
    clientId,
    clientRecord
}) => {
    const [initialSelfRegistrationSetting, setInitialSelfRegistrationSetting] =
        useState({});
    const [selectedSelfRegistration, setSelectedSelfRegistration] = useState();
    const [isOpen, setIsOpen] = useState(true);
    const { hasAttribute, clientTierMeetsRequirement } = useAuthContext();

    const enableForm = clientTierMeetsRequirement(TenantTiers.Bronze, clientRecord.tenantTierId)

    const getOptions = async () => {
        try {
            const data = await getClientOptions(clientId, [
                ClientOptionEnum.EnableUserSelfRegistration,
            ]);

            const selfRegistrationObject = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.EnableUserSelfRegistration,
            );
            if (selfRegistrationObject) {
                setInitialSelfRegistrationSetting(selfRegistrationObject);
                const isEnabled = selfRegistrationObject.value === 'true';
                setSelectedSelfRegistration(selfRegistrationObject.value);
                if (onSelfRegistrationChange) {
                    onSelfRegistrationChange(isEnabled);
                }
            } else {
                Toast.error(
                    getText('msg_fe96fc30ca96'),
                );
            }
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    useEffect(() => {
        getOptions();
    }, []);

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    const handleToggle = () => {
        const newValue = selectedSelfRegistration === 'true' ? 'false' : 'true';
        setSelectedSelfRegistration(newValue);
        if (onSelfRegistrationChange) {
            onSelfRegistrationChange(newValue === 'true');
        }
    };

    const handleSave = async () => {
        let noChanges = true;

        if (initialSelfRegistrationSetting.usingDefaultValue) {
            noChanges = false;
            const clientOptionId =
                initialSelfRegistrationSetting.clientOptionId ??
                initialSelfRegistrationSetting.id ??
                ClientOptionEnum.EnableUserSelfRegistration;
            const option = {
                clientOptionId,
                value: `${selectedSelfRegistration}`,
            };
            const response = await createClientOption(option, clientId);
            if (response.error)
                return Toast.error(
                    getText('msg_668cade5a006'),
                );
            setInitialSelfRegistrationSetting(response.data);
        } else if (
            initialSelfRegistrationSetting.value !=
            `${selectedSelfRegistration}`
        ) {
            noChanges = false;
            const clientOptionId =
                initialSelfRegistrationSetting.clientOptionId ??
                initialSelfRegistrationSetting.id ??
                ClientOptionEnum.EnableUserSelfRegistration;
            const option = {
                clientOptionId,
                value: `${selectedSelfRegistration}`,
            };
            const response = await updateClientOption(option, clientId);
            if (response.error)
                return Toast.error(
                    getText('msg_0aae1d7085b5'),
                );
            setInitialSelfRegistrationSetting(response.data);
        }

        if (noChanges) Toast.warning(getText('msg_13c3a7ac4e47'));
        else Toast.success(getText('msg_98d749177308'));
    };

    const handleCancel = () => {
        const originalValue = initialSelfRegistrationSetting.value;
        setSelectedSelfRegistration(originalValue);
        if (onSelfRegistrationChange) {
            onSelfRegistrationChange(originalValue === 'true');
        }
    };

    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                marginTop: '10px',
                boxShadow:
                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
            }}
        >
            <div
                style={{
                    overflowY: 'auto',
                    padding: '16px',
                    resize: 'none',
                    boxShadow:
                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px;',
                }}
            >
                <div>
                    <div style={{ marginLeft: '12px' }}>
                        {getText('msg_2572f21bf4b1')}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                float: 'inline-end',
                                cursor: 'pointer',
                            }}
                            className={
                                'bi bi-chevron-' + (isOpen ? 'up' : 'down')
                            }
                            onClick={() => toggleAccordion()}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div
                        style={{
                            marginLeft: '48px',
                            marginRight: '48px',
                            marginTop: '12px',
                            color: 'var(--iNGAGED-Neutral-Color2)',
                        }}
                    >
                        <div style={{ marginTop: '12px' }}>
                            {getText('msg_bcaa45658579')}
                        </div>

                        <div style={{ marginLeft: '24px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                }}
                            >
                                <div className={`form-switch`}>
                                    <input
                                        onClick={handleToggle}
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedSelfRegistration === 'true'
                                        }
                                        disabled={!enableForm}
                                    />
                                </div>
                                <div>{getText('msg_4149e51842a7')}</div>
                            </div>
                        </div>
                        {hasAttribute(
                            SecurityAttributes.ClientSettingsFullAccess
                        ) && (
                                <div
                                    className={`d-flex justify-content-evenly mt-5`}
                                >
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="ing-popup-blue-negative"
                                        disabled={!enableForm}
                                    >
                                        {getText('msg_77dfd2135f4d')}
                                    </button>
                                    <button
                                        type="button"
                                        className={enableForm ? "ing-button-primary-none" : "ing-popup-blue-negative"}
                                        onClick={handleSave}
                                        disabled={!enableForm}
                                    >
                                        {getText('msg_efc007a393f6')}
                                    </button>
                                </div>
                            )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientSettingSelfRegistration;
